import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControl, FormControlLabel, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import classNames from 'classnames';

import { ManualNotificationEmailFormat, ManualNotificationsItemUpdateInput } from 'api';
import { ValidatedTextField } from 'components';
import { EMPTY_SLATE_EDITOR } from 'app-constants';

import SlateEditor from 'components/SlateEditor';

const useStyles = makeStyles(theme => ({
  secondaryPadding: {
    padding: `${theme.spacing(1)}px ${theme.spacing(8)}px 0`,
  },
  labelRoot: {
    marginLeft: -theme.spacing(1),
  },
  label: {
    fontSize: 14,
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const EmailInput: FC = () => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<ManualNotificationsItemUpdateInput>();
  const { t } = useTranslation();

  const { message, email } = values;
  const hasEmail = !!email;
  const emailBodyFieldName = 'email.body';

  const labelClasses = {
    root: classes.labelRoot,
    label: classNames(classes.label, {
      [classes.bold]: hasEmail,
    }),
  };

  function toggleEmail() {
    const emailValues = {
      format: ManualNotificationEmailFormat.Html,
      subject: email?.subject || message?.body || '',
      body: email?.body ?? EMPTY_SLATE_EDITOR,
    };

    const value = hasEmail ? null : emailValues;
    setFieldValue('email', value);
  }

  function renderFields() {
    if (!hasEmail) {
      return null;
    }

    return (
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item>
          <ValidatedTextField
            persistLabel
            color="primary"
            name="email.subject"
            i18nKey="messages:subject"
          />
        </Grid>
        <Grid item>
          <SlateEditor
            fieldName={emailBodyFieldName}
            label={t('messages:message')}
            placeholder={t('messages:composeEmail')}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Fragment>
      <FormControl margin="normal">
        <FormControlLabel
          label={t('messages:sendEmailNotifications')}
          classes={labelClasses}
          control={<Checkbox checked={hasEmail} onChange={toggleEmail} />}
        />
      </FormControl>
      <div className={classes.secondaryPadding}>{renderFields()}</div>
    </Fragment>
  );
};

export default EmailInput;
